import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const ShareToTeamModal = ({ record, onClose }) => {
  const [teams, setTeams] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    loadUserTeams();
  }, []);

  const loadUserTeams = async () => {
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists() && userDoc.data().teams) {
        const teamIds = userDoc.data().teams;
        const teamPromises = teamIds.map(teamId => 
          getDoc(doc(db, 'teams', teamId))
        );
        const teamDocs = await Promise.all(teamPromises);
        const teamsData = teamDocs
          .filter(doc => doc.exists())
          .map(doc => ({ id: doc.id, ...doc.data() }));
        
        setTeams(teamsData);
      }
    } catch (error) {
      console.error('載入團隊失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTeamToggle = (teamId) => {
    setSelectedTeams(prev => 
      prev.includes(teamId) 
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  const handleShare = async () => {
    if (selectedTeams.length === 0) {
      alert('請選擇至少一個團隊');
      return;
    }

    setSharing(true);
    try {
      const recordRef = doc(db, 'businessCardsnotenew', record.id);
      const currentSharedTeams = record.sharedWithTeams || [];
      const newSharedTeams = [...new Set([...currentSharedTeams, ...selectedTeams])];
      
      await updateDoc(recordRef, {
        sharedWithTeams: newSharedTeams,
        lastShared: new Date(),
        sharedBy: auth.currentUser.uid
      });
      
      alert('名片已成功分享到所選團隊！');
      onClose();
    } catch (error) {
      console.error('分享失敗:', error);
      alert('分享失敗，請重試');
    } finally {
      setSharing(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="loading-message">載入中...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>分享名片到團隊</h3>
          <button onClick={onClose} className="close-btn">✕</button>
        </div>
        
        <div className="modal-body">
          <div className="record-preview">
            <h4>要分享的名片</h4>
            <div className="preview-card">
              <div className="preview-name">
                {record['姓名'] || record['Name'] || '未知'}
              </div>
              <div className="preview-company">
                {record['公司名稱'] || record['Company'] || ''}
              </div>
              <div className="preview-title">
                {record['職位'] || record['Title'] || ''}
              </div>
            </div>
          </div>

          {teams.length === 0 ? (
            <div className="no-teams">
              <p>您還沒有加入任何團隊</p>
              <p>請先創建或加入團隊才能分享名片</p>
            </div>
          ) : (
            <div className="teams-section">
              <h4>選擇要分享的團隊</h4>
              <div className="teams-list">
                {teams.map(team => (
                  <label key={team.id} className="team-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedTeams.includes(team.id)}
                      onChange={() => handleTeamToggle(team.id)}
                    />
                    <div className="team-info">
                      <div className="team-name">{team.name}</div>
                      <div className="team-members">{team.members.length} 成員</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="cancel-btn">
            取消
          </button>
          {teams.length > 0 && (
            <button 
              onClick={handleShare}
              disabled={sharing || selectedTeams.length === 0}
              className="share-btn"
            >
              {sharing ? '分享中...' : '分享'}
            </button>
          )}
        </div>

        <style jsx="true">{`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.7);
            backdrop-filter: blur(5px);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 2000;
            padding: 20px;
          }

          .modal-content {
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(20px);
            border-radius: 24px;
            border: 1px solid rgba(255, 255, 255, 0.6);
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
            max-width: 500px;
            width: 100%;
            max-height: 80vh;
            overflow: hidden;
            display: flex;
            flex-direction: column;
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px 24px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.3);
            background: rgba(255, 255, 255, 0.1);
          }

          .modal-header h3 {
            margin: 0;
            color: rgba(108, 99, 143, 0.9);
            font-size: 18px;
            font-weight: 600;
          }

          .close-btn {
            background: none;
            border: none;
            font-size: 18px;
            cursor: pointer;
            color: rgba(108, 99, 143, 0.6);
            padding: 4px 8px;
            border-radius: 6px;
            transition: all 0.2s ease;
          }

          .close-btn:hover {
            background: rgba(255, 255, 255, 0.3);
            color: rgba(108, 99, 143, 0.9);
          }

          .modal-body {
            padding: 20px 24px;
            overflow-y: auto;
            flex: 1;
          }

          .record-preview {
            margin-bottom: 24px;
          }

          .record-preview h4 {
            margin: 0 0 12px 0;
            color: rgba(108, 99, 143, 0.9);
            font-size: 16px;
            font-weight: 600;
          }

          .preview-card {
            background: rgba(255, 255, 255, 0.3);
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255, 255, 255, 0.4);
            border-radius: 12px;
            padding: 16px;
            text-align: center;
          }

          .preview-name {
            font-size: 18px;
            font-weight: 600;
            color: rgba(108, 99, 143, 0.9);
            margin-bottom: 4px;
          }

          .preview-company {
            font-size: 14px;
            color: rgba(108, 99, 143, 0.7);
            margin-bottom: 2px;
          }

          .preview-title {
            font-size: 12px;
            color: rgba(108, 99, 143, 0.6);
          }

          .no-teams {
            text-align: center;
            padding: 40px 20px;
            color: rgba(108, 99, 143, 0.7);
          }

          .no-teams p {
            margin: 8px 0;
            font-size: 14px;
          }

          .teams-section h4 {
            margin: 0 0 12px 0;
            color: rgba(108, 99, 143, 0.9);
            font-size: 16px;
            font-weight: 600;
          }

          .teams-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }

          .team-checkbox {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px 16px;
            background: rgba(255, 255, 255, 0.3);
            border: 1px solid rgba(255, 255, 255, 0.4);
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .team-checkbox:hover {
            background: rgba(255, 255, 255, 0.4);
            transform: translateY(-1px);
          }

          .team-checkbox input[type="checkbox"] {
            width: 18px;
            height: 18px;
            cursor: pointer;
          }

          .team-info {
            flex: 1;
          }

          .team-name {
            font-weight: 600;
            color: rgba(108, 99, 143, 0.9);
            font-size: 14px;
            margin-bottom: 2px;
          }

          .team-members {
            font-size: 12px;
            color: rgba(108, 99, 143, 0.6);
          }

          .modal-footer {
            display: flex;
            gap: 12px;
            justify-content: flex-end;
            padding: 20px 24px;
            border-top: 1px solid rgba(255, 255, 255, 0.3);
            background: rgba(255, 255, 255, 0.1);
          }

          .cancel-btn {
            background: rgba(108, 117, 125, 0.8);
            color: white;
            border: none;
            padding: 10px 20px;
            border-radius: 8px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            transition: all 0.2s ease;
          }

          .cancel-btn:hover {
            background: rgba(108, 117, 125, 1);
            transform: translateY(-1px);
          }

          .share-btn {
            background: linear-gradient(135deg, #673ab7 0%, #9c27b0 100%);
            color: white;
            border: none;
            padding: 10px 20px;
            border-radius: 8px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            transition: all 0.2s ease;
            box-shadow: 0 2px 8px rgba(103, 58, 183, 0.3);
          }

          .share-btn:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(103, 58, 183, 0.4);
          }

          .share-btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }

          .loading-message {
            text-align: center;
            padding: 40px;
            color: rgba(108, 99, 143, 0.8);
            font-size: 16px;
          }

          @media (max-width: 768px) {
            .modal-content {
              max-height: 90vh;
              margin: 20px;
            }

            .modal-footer {
              flex-direction: column;
            }

            .cancel-btn, .share-btn {
              width: 100%;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default ShareToTeamModal;